import { generateId } from '@monoimin/utils/helpers';
import { isBot } from '@monoimin/utils/analytics';

import { safeLocalStorage } from 'libs/utils/src/lib/storage/safeLocalStorage';

import {
  getSessionStorageItem,
  setSessionStorageItem,
} from 'libs/utils/src/lib/storage/sessionStorage';
import { IS_ANALYTICS_ENABLED } from '@monoimin/constants';

import { getRootDomain } from './getRootDomain';
import { formatErrorMessage, logError } from './logError';
import {
  AmplitudeEvent,
  AmplitudeEventProperties,
  AmplitudeFormSubmittedError,
  SendRequestPayload,
} from './types';

const ANALYTICS_ENDPOINT = 'https://ream.immigrantinvest.com/collect/v1';

const waitForAmplitude = (maxWaitTime = 2000) => {
  return new Promise<boolean>((resolve) => {
    if (
      typeof window !== 'undefined' &&
      window?.amplitude &&
      typeof window.amplitude?.getDeviceId === 'function'
    ) {
      resolve(true);
      return;
    }

    // Wait for Amplitude to initialize
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (
        typeof window !== 'undefined' &&
        window?.amplitude &&
        typeof window.amplitude?.getDeviceId === 'function'
      ) {
        clearInterval(checkInterval);
        resolve(true);
      } else if (Date.now() - startTime > maxWaitTime) {
        clearInterval(checkInterval);
        resolve(false);
      }
    }, 100);
  });
};

const processEventProperties = (event: AmplitudeEvent) => {
  const properties = event.properties || {};

  const processedProperties: AmplitudeEventProperties = {
    ...properties,
    site_domain: getRootDomain(window.location.hostname),
    site_hostname: window.location.hostname,
    site_language: properties['language'],
    page_url: window.location.href,
  };

  delete processedProperties['language'];

  if (event.name.includes('CTA')) {
    delete processedProperties['ipcountry'];
    delete processedProperties['ipcity'];
    delete processedProperties['country'];
    delete processedProperties['city'];

    if (event.name === 'CTA Block View') {
      delete processedProperties['block_element_click'];
      delete processedProperties['element_click_url'];
    }
  }

  return processedProperties;
};

export const getDeviceInfo = () => {
  const DEFAULT_DEVICE_ID_PREFIX = '9999';
  const DEVICE_ID_KEY = 'device_id';
  const SESSION_ID_KEY = 'session_id';

  const generateDeviceId = () => DEFAULT_DEVICE_ID_PREFIX + generateId(28);
  const generateSessionId = () => Date.now().toString();

  const isAmplitudeAvailable =
    typeof window !== 'undefined' &&
    window?.amplitude &&
    typeof window.amplitude?.getDeviceId === 'function';

  const getOrGenerateDeviceId = () => {
    if (!isAmplitudeAvailable) {
      let deviceId = safeLocalStorage.getItem(DEVICE_ID_KEY) || '';
      if (deviceId) return deviceId;
      deviceId = generateDeviceId();
      safeLocalStorage.setItem(DEVICE_ID_KEY, deviceId);
      return deviceId;
    }
    const existingDeviceId = window.amplitude.getDeviceId();
    if (existingDeviceId) {
      return existingDeviceId;
    }
    let deviceId = safeLocalStorage.getItem(DEVICE_ID_KEY) || '';
    if (!deviceId) {
      deviceId = generateDeviceId();
      safeLocalStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    window.amplitude.setDeviceId(deviceId);
    return deviceId;
  };

  const getOrGenerateSessionId = () => {
    if (!isAmplitudeAvailable) {
      let sessionId = getSessionStorageItem(SESSION_ID_KEY) || '';
      if (sessionId) return sessionId;
      sessionId = generateSessionId();
      setSessionStorageItem(SESSION_ID_KEY, sessionId);
      return sessionId;
    }
    const existingSessionId = window.amplitude.getSessionId()?.toString();
    if (existingSessionId) return existingSessionId;
    const newSessionId = generateSessionId();
    window.amplitude.setSessionId(newSessionId);
    return newSessionId;
  };

  return {
    deviceId: getOrGenerateDeviceId(),
    sessionId: getOrGenerateSessionId(),
  };
};

const setAmplitudeUserId = (userId: string | null): string | null => {
  const isAmplitudeAvailable =
    typeof window !== 'undefined' &&
    window?.amplitude &&
    typeof window.amplitude?.setUserId === 'function' &&
    typeof window.amplitude?.getUserId === 'function';

  if (!isAmplitudeAvailable) return null;

  const currentUserId = window.amplitude.getUserId();

  // Return existing userId if it exists
  if (currentUserId) {
    return currentUserId;
  }

  // Set and return new userId if provided
  if (userId) {
    window.amplitude.setUserId(userId);
    return userId;
  }
  return null;
};

const sendRequest = async (
  payload: SendRequestPayload
): Promise<Response | null> => {
  const hasDeviceId = 'deviceId' in payload && payload.deviceId;
  try {
    const response = await fetch(ANALYTICS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      keepalive: true,
      body: JSON.stringify(payload),
    });

    // Clone response before consuming
    const responseClone = response.clone();
    const rawBody = await responseClone.text();

    let parsedBody;
    try {
      parsedBody = JSON.parse(rawBody);
    } catch {
      parsedBody = rawBody;
    }

    if (!response.ok && hasDeviceId) {
      const errorMessage =
        formatErrorMessage(parsedBody?.error) ||
        `HTTP error status: ${response.status}`;
      const error = new Error(errorMessage);

      logError(error, {
        ...payload,
        responseStatus: response.status,
        errorDetails: parsedBody,
        errorType: 'APIError',
      });
      return null;
    }

    return parsedBody;
  } catch (error) {
    if (hasDeviceId) {
      logError(error, {
        ...payload,
        errorType: 'NetworkError',
        errorMessage: formatErrorMessage(error),
        responseStatus: error instanceof Error ? undefined : 500,
      });
    }

    return null;
  }
};

export const trackAmplitudeEvent = async (
  event: AmplitudeEvent,
  enableDebuggingLogs?: boolean,
  sendDataToAmplitudeDevEnv?: boolean
) => {
  // если не включена аналитика, то ничего не делаем
  if (!IS_ANALYTICS_ENABLED) return;

  // если это бот, то ничего не делаем
  if (isBot()) return;

  try {
    await waitForAmplitude();

    const { deviceId, sessionId } = getDeviceInfo();
    const userId = setAmplitudeUserId(event?.userId);

    const processedProperties = processEventProperties(event);

    const payload = {
      service: 'amplitude',
      ...event,
      userId: userId ?? event.userId,
      deviceId,
      sessionId,
      properties: processedProperties,
      sendDataToAmplitudeDevEnv,
    };
    if (enableDebuggingLogs) {
      console.log('Amplitude Event Payload:', payload);
    }

    // Push entire payload to dataLayer for Google Tag Manager
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    }

    await sendRequest(payload);
  } catch (error) {
    logError(error, {
      ...event,
      errorType: 'UnhandledError',
      errorMessage: formatErrorMessage(error),
    });
  }
};
interface TrackAmplitudeQuizEvent {
  name: 'Element Quiz Click' | 'Page Quiz View';
  quiz_page_type?:
    | 'question'
    | 'first_screen'
    | 'success_screen'
    | 'fail_screen';
  additionalProperties?: Partial<{
    quiz_answer_type: 'button' | 'select' | 'radio';
    quiz_answers_count: number;
    quiz_question_text: string;
    quiz_answer_selected: string;
    quiz_question_number: number;
    [key: string]: any;
  }>;
}

export async function trackAmplitudeQuizEvent({
  name,
  quiz_page_type,
  additionalProperties,
}: TrackAmplitudeQuizEvent) {
  const event: AmplitudeEvent = {
    deviceId: '',
    userId: null,
    name: name,
    properties: {
      quiz_page_type,
      quiz_name: window.location.pathname,
      ...additionalProperties,
    },
  };
  // в window.eventHashes сохраняем хэш события, чтобы не отправлять его повторно
  const eventHash = generateHash(JSON.stringify(event));
  if (window.eventHashes && window.eventHashes[eventHash]) return;
  window.eventHashes = window.eventHashes || {};
  window.eventHashes[eventHash] = true;

  return trackAmplitudeEvent(event, true);
}
export const trackAmplitudeFormSubmittedError = async (
  event: AmplitudeFormSubmittedError
) => {
  if (!IS_ANALYTICS_ENABLED) return;

  const { deviceId, sessionId } = getDeviceInfo();

  const payload = {
    service: 'amplitude',
    deviceId,
    sessionId,
    userId: null,
    name: 'Form Submitted Error',
    properties: {
      error_type: event.error_type,
      error_fields: event.error_fields,
      error_status_code: event.error_status_code,
      form_client_segment: event.form_client_segment,
      form_ui_element: event.form_ui_element,
      form_id: event.form_id,
      form_type: event.form_type,
      form_name: event.form_name,
    },
  };

  // Push entire payload to dataLayer for Google Tag Manager
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];

    const dataLayerPayload = {
      ...payload,
      // Convert array to comma-separated string for dataLayer
      error_fields: Array.isArray(event.error_fields)
        ? event.error_fields.join(',')
        : '',
    };

    // Push to dataLayer
    window.dataLayer.push(dataLayerPayload);
  }

  await sendRequest(payload);
};

/**
 * Browser-compatible hash function for generating event hashes
 * Uses a combination of djb2 and sdbm algorithms for better distribution
 */
const generateHash = (input: string): string => {
  let hash1 = 5381; // djb2
  let hash2 = 0; // sdbm

  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash1 = (hash1 << 5) + hash1 + char; // djb2: hash * 33 + char
    hash2 = char + (hash2 << 6) + (hash2 << 16) - hash2; // sdbm
  }

  // Combine both hashes and convert to positive hex
  const combined = Math.abs(hash1) ^ Math.abs(hash2);
  return combined.toString(16);
};
